import { useCallback, useMemo, useRef, useState } from 'react';

/** Keep in sync with placement math in handleOpen */
export const MENU_PAPER_MAX_HEIGHT_PX = 360;

/**
 * State + handlers for `NRAutocomplete` (MUI Select + filter + optional virtualization).
 * Selection is owned only by `handleOptionToggle` / parent `setValue`; Select `onChange` is a no-op.
 * UI stays in `NRAutocomplete.jsx`; this file is logic-only.
 */
export function useNRAutocompleteSelect({
  arrayData = [],
  value = [],
  setValue,
  loading,
  dataNameSingular = 'Group',
  fieldToShow = 'name',
  idField = 'id',
  multiple = true,
  disabled = false,
  assetsAndGroups = false,
  onOpenChange = () => {},
  helperText = '',
  virtualizationThreshold = 120
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [menuPlacement, setMenuPlacement] = useState('bottom');
  const selectContainerRef = useRef(null);
  const menuPaperRef = useRef(null);
  const filterInputRef = useRef(null);
  const optionRowRefs = useRef(new Map());
  const assetGroupFirstButtonRef = useRef(null);
  const assetGroupLastButtonRef = useRef(null);

  const selectedList = multiple ? (Array.isArray(value) ? value : []) : value?.[0] ? [value[0]] : [];
  const selectedIds = useMemo(() => selectedList.map(v => (v && typeof v === 'object' ? v[idField] : v)), [selectedList, idField]);
  const selectedIdSet = useMemo(() => new Set(selectedIds.map(id => String(id))), [selectedIds]);

  const arrayDataById = useMemo(() => {
    const m = new Map();
    (arrayData || []).forEach(item => {
      m.set(String(item[idField]), item);
    });
    return m;
  }, [arrayData, idField]);

  const sortedOptions = useMemo(() => {
    const topOptionId = '';
    const topOptions = [];
    const rest = [];
    const seenIds = new Set();

    arrayData.forEach(item => {
      const currentId = item[idField];
      if (seenIds.has(currentId)) return;
      seenIds.add(currentId);
      if (currentId === topOptionId) topOptions.push(item);
      else rest.push(item);
    });

    const sortByField = (a, b) => String(a[fieldToShow] || '').localeCompare(String(b[fieldToShow] || ''));
    rest.sort(sortByField);
    return [...topOptions, ...rest];
  }, [arrayData, idField, fieldToShow]);

  const filteredOptions = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sortedOptions;
    return sortedOptions.filter(item =>
      String(item[fieldToShow] || '')
        .toLowerCase()
        .includes(q)
    );
  }, [sortedOptions, filter, fieldToShow]);

  const displayValue = useMemo(() => {
    if (!arrayData?.length) {
      if (loading) return `Loading ${dataNameSingular}s...`;
      return '';
    }
    if (selectedList.length === 1) {
      if (multiple) return `1 ${dataNameSingular} selected`;
      const v = selectedList[0];
      if (typeof v === 'object' && v !== null) return v[fieldToShow];
      return arrayDataById.get(String(v))?.[fieldToShow] ?? v;
    }
    if (selectedList.length > 1) return `${selectedList.length} ${dataNameSingular}s selected`;
    return '';
  }, [selectedList, arrayData, fieldToShow, dataNameSingular, loading, multiple, arrayDataById]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setFilter('');
    onOpenChange(false);
  }, [onOpenChange]);

  const handleOpen = useCallback(() => {
    if (disabled || !arrayData?.length) return;
    const rect = selectContainerRef.current?.getBoundingClientRect?.();
    if (rect) {
      const rowHeight = 44;
      const filterHeaderHeight = 72;
      const estimatedMenuHeight = Math.min(MENU_PAPER_MAX_HEIGHT_PX, filteredOptions.length * rowHeight + filterHeaderHeight);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUpward = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      setMenuPlacement(shouldOpenUpward ? 'top' : 'bottom');
    }
    setFilter('');
    setOpen(true);
    onOpenChange(true);
    const focusInitial = () => {
      if (assetsAndGroups) {
        assetGroupFirstButtonRef.current?.focus?.();
      } else {
        filterInputRef.current?.focus?.();
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(focusInitial);
    });
  }, [disabled, arrayData?.length, filteredOptions, onOpenChange, assetsAndGroups]);

  const registerOptionRowRef = useCallback((optionId, el) => {
    const key = String(optionId);
    if (el) optionRowRefs.current.set(key, el);
    else optionRowRefs.current.delete(key);
  }, []);

  const getFocusableOptionElements = useCallback(() => {
    return filteredOptions
      .map(o => optionRowRefs.current.get(String(o[idField])))
      .filter(el => {
        if (!(el instanceof HTMLElement)) return false;
        if (el.getAttribute('aria-disabled') === 'true') return false;
        return true;
      });
  }, [filteredOptions, idField]);

  const refocusOptionRow = useCallback(optionId => {
    const key = String(optionId);
    const el = optionRowRefs.current.get(key);
    if (el && typeof el.focus === 'function') {
      el.focus();
    }
  }, []);

  const queueRefocusOptionRow = useCallback(
    optionId => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          refocusOptionRow(optionId);
        });
      });
    },
    [refocusOptionRow]
  );

  const handleOptionToggle = useCallback(
    optionId => {
      if (optionId === '' || optionId === undefined) {
        setValue([]);
        return;
      }

      if (multiple) {
        const nextSet = new Set(selectedIds.map(id => String(id)));
        const key = String(optionId);
        if (nextSet.has(key)) nextSet.delete(key);
        else nextSet.add(key);
        const next = arrayData.filter(item => nextSet.has(String(item[idField])));
        setValue(next);
        queueRefocusOptionRow(key);
        return;
      }

      const selected = arrayData.find(item => String(item[idField]) === String(optionId));
      setValue(selected ? [selected] : []);
      handleClose();
    },
    [arrayData, handleClose, multiple, queueRefocusOptionRow, selectedIds, setValue]
  );

  const clearAllSelections = useCallback(() => {
    setValue([]);
    setOpen(false);
    setFilter('');
    onOpenChange(false);
  }, [onOpenChange, setValue]);

  const selectDisabled = disabled || !arrayData?.length;
  const showClear = !!selectedList.length;
  const useVirtualizedList = filteredOptions.length > virtualizationThreshold;
  const focusMenuOption = useCallback(
    focusLast => {
      const run = () => {
        const els = getFocusableOptionElements();
        if (!els.length) return;
        const target = focusLast ? els[els.length - 1] : els[0];
        target.focus();
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(run);
      });
    },
    [getFocusableOptionElements]
  );

  const focusFilterInput = useCallback(() => {
    const el = filterInputRef.current;
    if (el && typeof el.focus === 'function') el.focus();
  }, []);

  const focusLastAssetGroupButton = useCallback(() => {
    assetGroupLastButtonRef.current?.focus?.();
  }, []);

  /** MUI Select is controlled via `value` + parent updates; all selection changes go through `handleOptionToggle`. */
  const handleSelectChangeNoop = useCallback(() => {}, []);

  const handleFilterKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        focusMenuOption(false);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        if (assetsAndGroups) {
          focusLastAssetGroupButton();
        }
        return;
      }
      const isPrintable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
      if (isPrintable || e.key === 'Backspace' || e.key === 'Delete') {
        e.stopPropagation();
      }
    },
    [assetsAndGroups, focusLastAssetGroupButton, focusMenuOption, handleClose]
  );

  const handleMenuListKeyDown = useCallback(
    e => {
      if (e.key === 'Tab' && !e.shiftKey) {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;
        const filterEl = filterInputRef.current;
        if (!filterEl || target !== filterEl) return;
        const opts = getFocusableOptionElements();
        if (!opts.length) return;
        e.preventDefault();
        focusMenuOption(false);
        return;
      }
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
        return;
      }
      if (e.key === 'Enter') {
        const activeEl = document.activeElement;
        const optionId = activeEl?.getAttribute?.('data-option-id');
        if (optionId !== null && optionId !== undefined) {
          e.preventDefault();
          e.stopPropagation();
          handleOptionToggle(optionId);
        }
      }
    },
    [focusMenuOption, getFocusableOptionElements, handleClose, handleOptionToggle]
  );

  const handlePaperKeyDownCapture = useCallback(
    e => {
      if (e.key !== 'Tab' || e.shiftKey) return;
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const filterEl = filterInputRef.current;
      if (!filterEl || target !== filterEl) return;
      const opts = getFocusableOptionElements();
      if (!opts.length) return;
      e.preventDefault();
      focusMenuOption(false);
    },
    [focusMenuOption, getFocusableOptionElements]
  );

  return {
    open,
    filter,
    setFilter,
    menuPlacement,
    selectContainerRef,
    menuPaperRef,
    filterInputRef,
    assetGroupFirstButtonRef,
    assetGroupLastButtonRef,
    selectedList,
    selectedIds,
    selectedIdSet,
    filteredOptions,
    displayValue,
    selectDisabled,
    showClear,
    useVirtualizedList,
    MENU_PAPER_MAX_HEIGHT_PX,
    handleOpen,
    handleClose,
    handleOptionToggle,
    handleSelectChangeNoop,
    clearAllSelections,
    focusMenuOption,
    focusFilterInput,
    handleMenuListKeyDown,
    handlePaperKeyDownCapture,
    registerOptionRowRef,
    handleFilterKeyDown
  };
}
